import type { WidgetData, WidgetSourceRef } from './types';

/**
 * The live-data seam (doc-widgets README §D4) — "active graphs" without lying.
 *
 * A widget's stored payload is the audit record: what was recorded when the document was written.
 * A host MAY register a provider per `sourceRef.kind`; where one exists the renderer offers
 * Refresh and shows the provider's answer as a **clearly labeled overlay**. The stored values are
 * never overwritten — the same rule as the frozen glossary definitions and the immutable run
 * record. Liveness is a view, not an edit.
 *
 * The registry is host-global and deliberately tiny: kinds are opaque strings owned by hosts
 * ("pulse.quotes" in knowit-main is the first). A `sourceRef` whose kind has no provider renders
 * exactly like a plain widget — capability degrades to absence, never to an error.
 */
export interface WidgetLiveValues {
  /** label → current value. Labels not present stay at their stored rendering. */
  values: Record<string, number | null>;
  /** When the provider fetched them — rendered on the overlay label. */
  asOf: string;
}

export type WidgetDataProvider = (
  ref: WidgetSourceRef,
  data: WidgetData,
) => Promise<WidgetLiveValues>;

const providers = new Map<string, WidgetDataProvider>();

export function registerWidgetDataProvider(kind: string, provider: WidgetDataProvider): void {
  providers.set(kind, provider);
}

export function getWidgetDataProvider(kind: string): WidgetDataProvider | undefined {
  return providers.get(kind);
}
